/* Which language the cockpit is in, and where that choice is remembered.
 *
 * This module knows about a preference and a document attribute. It does not
 * import i18next, and nothing here calls `changeLanguage`. The dependency runs
 * one way: `config.ts` reads the stored preference to decide what to start in,
 * and subscribes to i18next's own `languageChanged` event to write it back. A
 * switcher therefore only ever calls `i18n.changeLanguage`, and persistence
 * follows as a consequence rather than as a second thing the caller must
 * remember to do.
 *
 * The shape deliberately mirrors `initializeScale.ts` in `@cloudorder/ui`,
 * which does the same job for the colour scheme: read a stored choice, fall
 * back to the system, apply it to the root element, and never throw when
 * storage is unavailable.
 */

/** The two languages the cockpit ships. Not a string: a locale the app has no
    catalogue for would render keys, and the type is what stops one arriving. */
export type Locale = 'en' | 'de';

export const LOCALES: readonly Locale[] = ['en', 'de'];

export const FALLBACK_LOCALE: Locale = 'en';

const STORAGE_KEY = 'cloudorder.locale';

export function isLocale(value: string | null | undefined): value is Locale {
  return value === 'en' || value === 'de';
}

/** The language part of a tag, so `de-CH` and `de-AT` both count as German.
    Region is not modelled: the catalogue is one German, and pretending
    otherwise would promise a Swiss variant that does not exist. */
function baseOf(tag: string): string {
  return (tag.split('-')[0] ?? tag).toLowerCase();
}

function readStored(): Locale | null {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isLocale(stored) ? stored : null;
  } catch {
    // Private browsing modes throw on localStorage access. No stored choice is
    // not an error; fall through to what the browser says.
    return null;
  }
}

/** The first of the browser's preferred languages the cockpit can actually
    speak. A reader who lists Dutch first and German second gets German, which
    is nearer their intent than the English fallback. */
function browserLocale(): Locale | null {
  if (typeof navigator === 'undefined') return null;
  // `languages` is absent on some older engines and on non-browser runtimes that
  // provide a partial `navigator`; `language` may be missing with it. Anything
  // that is not a string is skipped rather than parsed.
  const preferred = navigator.languages ?? [navigator.language];
  for (const tag of preferred) {
    if (typeof tag !== 'string') continue;
    const base = baseOf(tag);
    if (isLocale(base)) return base;
  }
  return null;
}

/** What to start in: the remembered choice, else the browser's, else English. */
export function initialLocale(): Locale {
  return readStored() ?? browserLocale() ?? FALLBACK_LOCALE;
}

/** Remember a choice so it survives a reload. Failure is silent by design —
    the language is applied either way, and only the memory of it is lost. */
export function rememberLocale(locale: Locale): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // Storage unavailable. The session is still in the right language.
    return;
  }
}

/** Put the language on the root element.
 *
 * Not cosmetic: `lang` is what a screen reader reads the page with, and what
 * the browser hyphenates and spell-checks against. A German page announced by
 * an English voice is the failure this prevents. */
export function applyDocumentLanguage(locale: Locale): void {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('lang', locale);
}

/** The locale currently applied to the document, for code that needs it
    outside React — CSV export, `Intl` formatters built ad hoc. Reads the
    attribute rather than a module variable so there is one source of truth. */
export function getLocale(): Locale {
  if (typeof document === 'undefined') return FALLBACK_LOCALE;
  const attr = document.documentElement.getAttribute('lang');
  return isLocale(attr) ? attr : FALLBACK_LOCALE;
}
